import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { Router } from '@angular/router';
import { IModule } from 'src/app/interfaces/IModule';
import { DynamicQuestionnaireService } from 'src/app/services/dynamicQuestionnaire.service';
import { EpisodeOfCareService } from 'src/app/services/episodeOfCare.service';
import { PatientService } from 'src/app/services/patient.service';
import { QuestionnaireHistoryItem } from 'src/app/interfaces/Questionnaire/IQuestionnaireHistory';
import { getQuestionnaireTemplateConfigByPath } from 'src/app/constants/questionnaire-template-registry';
import { paths } from 'src/component-paths/component-paths';

@Component({
  selector: 'app-pilot-questionnaires',
  templateUrl: './pilot-questionnaires.component.html',
  styleUrl: './pilot-questionnaires.component.css',
  standalone: false,
})
export class PilotQuestionnairesComponent implements OnChanges {
  @Input() submenu?: IModule[];
  @Input() overviewItemsByTemplateIdInput: Record<
    number,
    QuestionnaireHistoryItem[]
  > | null = null;
  @Input() overviewLoadingInput: boolean | null = null;
  @Input() overviewErrorInput: string | null = null;
  @Input() selectedOption?: string;
  @Output() selectedOptionChange = new EventEmitter<string>();
  @Input() readonlyMode = false;
  componentPaths = paths;
  localOverviewLoading = false;
  localOverviewError = '';
  localOverviewItemsByTemplateId: Record<number, QuestionnaireHistoryItem[]> =
    {};

  constructor(
    private readonly questionnaireService: DynamicQuestionnaireService,
    private readonly eocService: EpisodeOfCareService,
    private readonly patientService: PatientService,
    private readonly router: Router,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (
      (changes['submenu'] ||
        changes['readonlyMode'] ||
        changes['selectedOption']) &&
      !this.usesExternalOverview()
    ) {
      this.loadOverview();
    }
  }

  handleOverviewRefreshRequested(): void {
    this.loadOverview();
  }

  get isOverviewLoading(): boolean {
    return this.overviewLoadingInput ?? this.localOverviewLoading;
  }

  get overviewError(): string {
    return this.overviewErrorInput ?? this.localOverviewError;
  }

  get overviewItemsByTemplateId(): Record<number, QuestionnaireHistoryItem[]> {
    return (
      this.overviewItemsByTemplateIdInput ??
      this.localOverviewItemsByTemplateId
    );
  }

  get activeCategoryModule(): IModule | undefined {
    return this.resolveActiveCategoryModule();
  }

  private loadOverview(): void {
    if (this.usesExternalOverview()) {
      return;
    }

    const patientId = this.patientService.patientIDsignal();
    const templateIds = this.collectTemplateIds(
      this.activeCategoryModule?.childModules,
    );

    if (!patientId || !templateIds.length) {
      this.localOverviewItemsByTemplateId = {};
      return;
    }

    this.localOverviewLoading = true;
    this.localOverviewError = '';

    this.questionnaireService
      .getQuestionnaireOverview(patientId, {
        templateIds,
        ...(this.isVisitMode()
          ? {
              visitId: this.eocService.currentVisitId() || undefined,
              episodeCareId: this.eocService.currentEocId() || undefined,
              includeDrafts: true,
            }
          : {}),
        section: paths.pilotQuestionnaires,
        forceRefresh: true,
      })
      .subscribe({
        next: (response) => {
          this.localOverviewItemsByTemplateId = response.templates.reduce(
            (
              accumulator: Record<number, QuestionnaireHistoryItem[]>,
              template,
            ) => {
              accumulator[template.questionnaireTemplateId] =
                template.items ?? [];
              return accumulator;
            },
            {},
          );
          this.localOverviewLoading = false;
        },
        error: () => {
          this.localOverviewItemsByTemplateId = {};
          this.localOverviewError = 'Could not load questionnaire overview.';
          this.localOverviewLoading = false;
        },
      });
  }

  private isVisitMode(): boolean {
    return (
      !!this.eocService.currentVisitId() &&
      (this.router.url.includes('episodeOfCare') ||
        this.router.url.includes('inPatientView'))
    );
  }

  private collectTemplateIds(modules?: IModule[]): number[] {
    return Array.from(
      new Set(
        modules
          ?.flatMap((module: IModule) => [
            getQuestionnaireTemplateConfigByPath(module.path)?.id,
            ...this.collectTemplateIds(module.childModules),
          ])
          .filter((id: number | undefined): id is number => !!id) ?? [],
      ),
    );
  }

  private resolveActiveCategoryModule(): IModule | undefined {
    if (!this.submenu?.length) {
      return undefined;
    }

    const selected = this.selectedOption;

    if (!selected || selected === paths.pilotQuestionnaires) {
      return this.submenu[0];
    }

    return (
      this.submenu.find((module: IModule) =>
        this.moduleContainsPath(module, selected),
      ) ?? this.submenu[0]
    );
  }

  private moduleContainsPath(module: IModule, selected: string): boolean {
    if (module.path === selected) {
      return true;
    }

    return (
      module.childModules?.some((child: IModule) =>
        this.moduleContainsPath(child, selected),
      ) ?? false
    );
  }

  private usesExternalOverview(): boolean {
    return this.overviewItemsByTemplateIdInput !== null;
  }
}